import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// MUI Components
import {
  Alert,
  Box,
  Button,
  IconButton,
  Snackbar,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';

// MUI Icons
import {
  CloudUpload,
  CloudDownload,
  Send as SendIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  PauseCircleFilled as PauseCircleFilledIcon,
  PlayCircleOutline as PlayCircleOutlineIcon
} from "@mui/icons-material";

// Contexts
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../contexts/ModalContext';

// Utilities & Helpers
import {
  addToSentItemsLSBulk,
  calcRefFees,
  calcWfs,
  cleanItemMetadataToPreventQuotaExceeded,
  fetchNextDataWithRetry,
  fetchWrapper,
  fetchWrapperNoAuth,
  mergeObjects,
  SERVER
} from '../../utils/helpers';
import { runBulkProcessSteps } from '../../utils/proccessHelpers';

// Components
import Form from '../../components/ProcessComponents/Form';
import FormSF from '../../components/ProcessComponents/FormSF';
import CustomStepper from '../../components/ProcessComponents/CustomStepper';
import ResizableSidebar from '../../components/SideBar/ResizableSidebar';
import ResponsiveItemCard from '../../components/SideBar/ResponsiveItemCard';

// Modals
import BatchSelectionModal from '../../components/Modals/BatchSelectionModal';
import BarcodeLabelModal from '../../components/Modals/BarcodeLabelModal';
import ErrorModal from '../../components/Modals/ErrorModal';
import NewBatchModal from '../../components/Modals/NewBatchModal';
import QuestionModal from '../../components/Modals/QuestionModal';
import ShippmentsModal from '../../components/Modals/ShippmentsModal';

// Tables
import SearchedProductsTable from '../../components/Tables/SearchedProductsTable';

// Tooltips & Loaders
import HtmlTooltip from '../../components/Tooltip/HtmlTooltip';
import CustomCircularProgress from '../../components/Loaders/CustomCircularProgress';

// Local storage keys
const STORAGE_KEY_ADDED_ITEMS = 'IFMarterBuyListsBulkProcess_addedItems';
const STORAGE_KEY_BATCH_INFO = 'IFMarterBuyListsBulkProcess_batchInfo';
const STORAGE_KEY_ACTIVE_STEP = 'IFMarterBuyListsBulkProcess_activeStep';
const STORAGE_KEY_CURR_PRODUCT = 'IFMarterBuyListsBulkProcess_currProduct';
const STORAGE_KEY = "IFMarterBuyListsBulkProcess";

function MarterBuyListsBulk(props) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { encodedAuth, user, password, isSubUser } = useAuth();
  const { showModal } = useModal();

  const [sidebarWidth, setSidebarWidth] = useState(320);

  const [barcodeLabelProduct, setBarcodeLabelProduct] = useState({});
  const [openBarcodeModal, setOpenBarcodeModal] = useState(false);
  const [shippmentOpenModal, setShippmentOpenModal] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openDeleteFromBatchModal, setOpenDeleteFromBatchModal] = useState(false);
  const [itemToModify, setItemToModify] = useState({});
  const [openErrorModal, setopenErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [alertBatch, setAlertBatch] = useState({ open: false, severity: "info", message: "" });

  const [batchesArray, setBatchesArray] = useState([]);
  const [addedItems, setAddedItems] = useState([]);
  const [channel, setChannel] = useState('wfs');
  const [batchName, setBatchName] = useState("My Marter's Buy List Bulk");
  const [activeStep, setActiveStep] = useState(1);
  const [currProduct, setCurrProduct] = useState(null);
  const [loadStates, setLoadStates] = useState(false);
  const [skuUpdate, setSkuUpdate] = useState(false);

  const [newBatchOpen, setNewBatchOpen] = useState(batchName === "");
  const steps = ['Select Batch Name', 'Select from buy list', 'Fill Product details', 'Start Proccess'];

  const [startProcess, setStartProcess] = useState(false);
  const [processSteps, setProcessSteps] = useState([
    { index: 0, name: "Upload to inventory as bulk", status: "pending", },
    { index: 1, name: "Check the bulk status", status: "pending", },
    { index: 2, name: "Check all items in bulk statuses", status: "pending", },
    { index: 3, name: "Convert all items to WFS in bulks by category", status: "pending", },
    { index: 4, name: "Check converting bulks statuses", status: "pending", },
    { index: 5, name: "Check all converting items", status: "pending", },
  ]);
  const processStatusIcons = {
    completed: <CheckCircleIcon sx={{ color: theme.accent.success, }} />, // Green check
    pending: <HourglassEmptyIcon sx={{ color: theme.accent.warning, }} />, // Yellow clock
    failed: <CancelIcon sx={{ color: theme.accent.error, }} />, // Red cross
    inProgress: <CircularProgress size={24} sx={{ color: theme.color.primary, }} />,
    warning: <WarningIcon sx={{ color: theme.accent.warning, }} />, // Orange warning
    actionRequired: <PauseCircleFilledIcon color="secondary" />, // Purple pause
  };

  const [prefetchStatus, setPrefetchStatus] = useState({});
  const [buyListItems, setBuyListItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Single useEffect to handle all localStorage updates
  useEffect(() => {
    if (loadStates) {
      const storage = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
      storage[batchName] = {
        addedItems: addedItems.map(cleanItemMetadataToPreventQuotaExceeded),
        channel: channel,
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(storage));
    }
  }, [loadStates, addedItems, channel]);

  const loadStatesFromLocalStorage = async (batchSelected) => {
    try {
      const storage = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
      setAddedItems((storage[batchSelected].addedItems || []).map(cleanItemMetadataToPreventQuotaExceeded));
      setChannel(storage[batchSelected].channel || "wfs");
      setBatchName(batchSelected || "My Marter's Buy List Bulk");
      setActiveStep(1);
      setLoadStates(true);
      setOpenConfirmModal(false);
    } catch (error) {
      console.error("Failed to load states from localStorage", error);
      // Ensure default values if an error occurs
      setAddedItems([]);
      setChannel("wfs");
      setBatchName("My Marter's Buy List Bulk");
      setActiveStep(1);
      setLoadStates(true);
      setOpenConfirmModal(false);
    }
  };

  // This useEffect runs once on component mount to load from localStorage
  useEffect(() => {
    const loadPrevData = async () => {
      try {
        const oldStorage = {
          addedItems: JSON.parse(localStorage.getItem(STORAGE_KEY_ADDED_ITEMS)) || [],
          batchInfo: JSON.parse(localStorage.getItem(STORAGE_KEY_BATCH_INFO)) || {},
        };
        if (oldStorage.addedItems.length > 0 && oldStorage.batchInfo?.batchName) {
          const newStorage = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
          newStorage[oldStorage.batchInfo?.batchName] = {
            addedItems: oldStorage.addedItems,
            channel: oldStorage.channel
          }
          localStorage.setItem(STORAGE_KEY, JSON.stringify(newStorage));
          localStorage.removeItem(STORAGE_KEY_ADDED_ITEMS);
          localStorage.removeItem(STORAGE_KEY_BATCH_INFO);
          localStorage.removeItem(STORAGE_KEY_ACTIVE_STEP);
          localStorage.removeItem(STORAGE_KEY_CURR_PRODUCT);
        }
      } catch (error) {
        console.error("Error checking localStorage", error);
      }
    }
    loadPrevData();

    const loadInitialState = async () => {
      try {
        const storage = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
        const batches = Object.keys(storage);
        if (batches.length > 0) {
          if (batches.includes("My Marter's Buy List Bulk") && storage["My Marter's Buy List Bulk"].addedItems.length === 0) {
            setLoadStates(true);
          } else {
            setBatchesArray(batches);
            setOpenConfirmModal(true);
          }
        } else {
          setLoadStates(true); // No stored data, enable storing right away
        }

        localStorage.removeItem(STORAGE_KEY_ADDED_ITEMS);
        localStorage.removeItem(STORAGE_KEY_BATCH_INFO);
        localStorage.removeItem(STORAGE_KEY_ACTIVE_STEP);
        localStorage.removeItem(STORAGE_KEY_CURR_PRODUCT);
      } catch (error) {
        console.error("Error checking localStorage", error);
        setLoadStates(true);
      }
    };

    loadInitialState();
  }, []);

  useEffect(() => {
    if (channel === "wfs") {
      setProcessSteps([
        { index: 0, name: "Upload to inventory as bulk", status: "pending", },
        { index: 1, name: "Check the bulk status", status: "pending", },
        { index: 2, name: "Check all items in bulk statuses", status: "pending", },
        { index: 3, name: "Convert all items to WFS in bulks by category", status: "pending", },
        { index: 4, name: "Check converting bulks statuses", status: "pending", },
        { index: 5, name: "Check all converting items", status: "pending", },
      ]);
    } else {
      setProcessSteps([
        { index: 0, name: "Upload to inventory as bulk", status: "pending", },
        { index: 1, name: "Check the bulk status", status: "pending", },
        { index: 2, name: "Check all items in bulk statuses", status: "pending", },
      ]);
    }
  }, [channel])

  // Clear localStorage data when items are successfully sent
  const clearStoredData = (batchSelected) => {
    const storage = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    delete storage[batchSelected];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storage));
    setBatchesArray((batches) => batches.filter(b => b !== batchSelected));
  };

  const showAlert = (severity, message) => {
    setAlertBatch({ open: true, severity, message });

    // Auto-close after 3 seconds
    setTimeout(() => {
      setAlertBatch({ ...alertBatch, open: false });
    }, 3000);
  };

  const handleSaveBatches = async () => {
    const batches = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    if (Object.keys(batches).length > 0 && !(Object.keys(batches).includes("My Marter's Buy List Bulk") && batches["My Marter's Buy List Bulk"].addedItems.length === 0)) {
      const body = {
        email: user,
        page: "MarterBuyListsBulk",
        batches: batches
      };
      try {
        await fetchWrapperNoAuth(`${SERVER}/falcon_save_batches`, user, password, { method: "POST", body: body });
        showAlert("success", "Batches have been successfully saved!");
      } catch (e) {
        showAlert("error", "An error occurred while saving batches. Please try again.");
      }
    } else {
      showAlert("warning", "No batches found to save. Please add some batches first.");
    }
  };

  const handleLoadBatches = async () => {
    const body = {
      email: user,
      page: "MarterBuyListsBulk"
    };
    try {
      const batches = await fetchWrapperNoAuth(`${SERVER}/falcon_load_batches`, user, password, { method: "POST", body: body });
      if (Object.keys(batches).length > 0) {
        setAddedItems([]);
        setChannel("wfs");
        setBatchName("My Marter's Buy List");
        setActiveStep(1);
        showAlert("success", "Batches have been successfully loaded! Refresh the page to view them.");
        const cleanedBatches = {};
        Object.keys(batches).forEach(batchName => {
          cleanedBatches[batchName] = {
            ...batches[batchName],
            addedItems: (batches[batchName].addedItems || []).map(cleanItemMetadataToPreventQuotaExceeded),
            searchedItems: (batches[batchName].searchedItems || []),
            channel: batches[batchName].channel
          };
        });
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cleanedBatches));
        window.location.reload();
      } else {
        showAlert("info", "No saved batches found in the cloud.");
      }
    } catch (e) {
      console.error(e);
      showAlert("error", "An error occurred while loading batches. Please try again.");
    }
  };

  const alertError = (msg) => {
    setErrorMessage(msg);
    setopenErrorModal(true);
  }

  const prefetchItemData = async (item) => {
    if (!item || prefetchStatus[item.itemId]) return;

    setPrefetchStatus(prev => ({
      ...prev,
      [item.itemId]: 'loading'
    }));

    let updatedItem = item;

    try {
      let nextData = await fetchNextDataWithRetry(user, password, item.itemId);
      if ((!nextData.length && !nextData.height && !nextData.width) || !nextData.weight || !nextData.manufacturer) {
        try {
          const azData = await fetchWrapperNoAuth(`${SERVER}/get_item_amazon_data/${item.upc || nextData.upc}`, user, password);
          if (azData.fromAz) {
            if ((!nextData.length && !nextData.height && !nextData.width) && (azData.length && azData.height && azData.width)) {
              nextData.dimsFromAz = true;
              nextData.length = azData.length;
              nextData.height = azData.height;
              nextData.width = azData.width;
            } else {
              nextData.dimsFromAz = false;
            }
            if (!nextData.weight && azData.weight) {
              nextData.weightFromAz = true;
              nextData.weight = azData.weight;
            } else {
              nextData.weightFromAz = false;
            }
            if (!nextData.manufacturer && azData.manufacturer) {
              nextData.manufacturerFromAz = true;
              nextData.manufacturer = azData.manufacturer;
            } else {
              nextData.manufacturerFromAz = false;
            }
            if (!nextData.brand && azData.brand) {
              nextData.brandFromAz = true;
              nextData.brand = azData.brand;
            } else {
              nextData.brandFromAz = false;
            }
          }
        } catch (error) {
          console.error(`Failed to prefetch data (get_amazon_bb_data) for UPC: ${item.upc}:`, error.message);
        }
      }
      updatedItem = mergeObjects(updatedItem, nextData);
    } catch (error) {
      console.error(`Failed to prefetch data (get_item_nextdata) for Item Id: ${item.itemId}:`, error.message);
    }

    const body = {
      'query': item.itemId
    };
    try {
      const items_search = await fetchWrapper(`${SERVER}/get_item_search`, user, password, encodedAuth, { method: 'POST', body: body });
      const item_search = items_search.filter((p) => p.itemId === item.itemId)[0];
      updatedItem = mergeObjects(item_search, updatedItem);
    } catch (error) {
      console.error(`Failed to prefetch data (get_item_search) for Item Id: ${item.itemId}:`, error.message);
    }

    try {
      setBuyListItems(prevItems =>
        prevItems.map(prevItem =>
          prevItem.itemId === item.itemId
            ? updatedItem
            : prevItem
        )
      );

      // Update currProduct if this is the currently selected item
      setCurrProduct(prevProduct =>
        prevProduct?.itemId === item.itemId
          ? updatedItem
          : prevProduct
      );

      setPrefetchStatus(prev => ({
        ...prev,
        [item.itemId]: 'completed'
      }));
    } catch (error) {
      console.error(`Failed to prefetch data for Item Id: ${item.itemId}:`, error.message);
      setPrefetchStatus(prev => ({
        ...prev,
        [item.itemId]: 'error'
      }));
    }
  };

  const handleAddItem = (product) => {
    if (!product) return;

    setAddedItems((prevItems) => {
      const existingIndex = prevItems.findIndex((item) => item.sku === product.sku);

      if (existingIndex !== -1) {
        // Replace the existing item
        return prevItems.map((item, index) =>
          index === existingIndex ? { ...product } : item
        );
      }

      // Add new product to the beginning
      return [{ ...product }, ...prevItems];
    });

    setBuyListItems((prevData) => {
      return prevData.filter((item) => {
        const identifier = product.itemId;
        const currentIdentifier = item.itemId;
        return currentIdentifier !== identifier;
      });
    });

    setCurrProduct({});
    setActiveStep(1);
  };

  const handleAddAnotherItem = () => {
    setActiveStep(1);
    setCurrProduct({});
  };

  const handleSendAllItems = () => {
    if (addedItems.length === 0) {
      alertError('First add items');
      return;
    }
    if (addedItems.every(item => item.status !== "LOADING")) {
      if (channel === "sf") {
        alertError('We have added all the items to your catalog as SF. Now, you need to go there and set the desired quantities(inventory). Next week, we will automate this as well! :)');
      } else {
        setShippmentOpenModal(true);
        // navigate('/shipment', { state: { batchName: batchName } });
      }
    } else {
      alertError('Please wait until all items have finished processing!');
    }
  };

  const removeFromBatch = async (item) => {
    // Remove from addedItems array
    setAddedItems((prevItems) => prevItems.filter((oldItem => oldItem.sku !== item.sku)));

    // Remove batchName from DB
    const metadata = {
      batchName: "none",
      email: user,
      productName: item.productName,
      image: item.image,
      sku: item.sku,
      gtin: item.gtin,
      upc: item.upc,
      cost: item.cost,
      qty: item.qty,
      innerPack: item.innerPack,
      source: item.source || "",
      note: item.note || "",
      expirationDate: item.expiration_date || null,
      inboundOrderId: item.inbound_order_id || "",
      shipmentId: item.shipment_id || "",
      purchaseDate: item.purchase_date || "",
      productType: item.product_type,
      schema: item.schema,
      MPItem: item.mp_item
    }
    const body = {
      sku: item.sku,
      email: user,
      batchName: "none",
      metadata: metadata
    }
    try {
      await fetchWrapperNoAuth(`${SERVER}/falcon_update_batch`, user, password, { method: 'POST', body: body });
    } catch (error) {
      console.error('Error update batch:', error.message);
    }
  };

  const editItem = (item) => {
    setCurrProduct(item);
    setActiveStep(2);
  };

  const retryAddItem = async (item) => {
    const url = `${SERVER}/send_item_feed_match`;
    const body = {
      item: {
        sku: item.sku,
        upc: item.upc,
        price: item.price.amount,
        weight: item.weight
      }
    }
    try {
      const { feedId } = await fetchWrapper(url, user, password, encodedAuth, { method: 'POST', body: body });
      // Create a new array by mapping through the current items
      const updatedItems = addedItems.map(existingItem => {
        // If the SKU matches, replace the item with the new one
        if (existingItem.sku === item.sku) {
          return {
            ...item,
            step: 1,
            feedId: feedId,
            status: "LOADING", // Reset the status
            retryCount: 0, // Increment retry count
            stepRetryCount: 0, // Increment step retry count
            lastRequest: new Date().toISOString() // Update the last request timestamp
          };
        }
        // Otherwise keep the existing item
        return existingItem;
      });

      // Update the state with the new array
      setAddedItems(updatedItems);
    } catch (error) { }
  };

  // Steps
  const handleNext = async (product) => {
    if (activeStep === 0) {
      if (!batchName) {
        alert("Create new Batch");
        setNewBatchOpen(true);
        return;
      }
    }

    if (activeStep === 1) {
      if (!product || Object.keys(product).length === 0) {
        alertError("Please select item")
        return;
      }
      setIsLoading(true);
      let tempProduct = product;

      // If nextData wasn't successfully prefetched, fetch it now
      if (prefetchStatus[product.itemId] !== 'completed' || !product.hasOwnProperty("length") || !product.hasOwnProperty("description")) {
        if (!product.hasOwnProperty("length")) {
          try {
            let nextData = await fetchNextDataWithRetry(user, password, product.itemId);
            if ((!nextData.length && !nextData.height && !nextData.width) || !nextData.weight || !nextData.manufacturer) {
              try {
                const azData = await fetchWrapperNoAuth(`${SERVER}/get_item_amazon_data/${product.upc || nextData.upc}`, user, password);
                if (azData.fromAz) {
                  if ((!nextData.length && !nextData.height && !nextData.width) && (azData.length && azData.height && azData.width)) {
                    nextData.dimsFromAz = true;
                    nextData.length = azData.length;
                    nextData.height = azData.height;
                    nextData.width = azData.width;
                  } else {
                    nextData.dimsFromAz = false;
                  }
                  if (!nextData.weight && azData.weight) {
                    nextData.weightFromAz = true;
                    nextData.weight = azData.weight;
                  } else {
                    nextData.weightFromAz = false;
                  }
                  if (!nextData.manufacturer && azData.manufacturer) {
                    nextData.manufacturerFromAz = true;
                    nextData.manufacturer = azData.manufacturer;
                  } else {
                    nextData.manufacturerFromAz = false;
                  }
                  if (!nextData.brand && azData.brand) {
                    nextData.brandFromAz = true;
                    nextData.brand = azData.brand;
                  } else {
                    nextData.brandFromAz = false;
                  }
                }
              } catch (error) {
                console.error(`Failed to prefetch data (get_amazon_bb_data) for UPC: ${product.upc}:`, error.message);
              }
            }
            tempProduct = mergeObjects(tempProduct, nextData);
          } catch (error) {
            console.error(`Failed to fetch from get_item_nextdata for Item Id: ${product.itemId}:`, error.message);
          }
        }
        if (!product.hasOwnProperty("description")) {
          const body = {
            'query': product.itemId
          };
          try {
            const items_search = await fetchWrapper(`${SERVER}/get_item_search`, user, password, encodedAuth, { method: 'POST', body: body });
            const item_search = items_search.filter((p) => p.itemId === product.itemId)[0];
            tempProduct = mergeObjects(item_search, tempProduct);
          } catch (error) {
            console.error(`Failed to prefetch data (get_item_search) for Item Id: ${product.itemId}:`, error.message);
          }
        }
      } else {
        // If nextData was prefetched, get the updated product from buyListItems
        tempProduct = buyListItems.find(item => item.itemId === product.itemId);
      }

      // Always fetch catalog search data when moving to next step
      try {
        const catalogSearch = await fetchWrapper(`${SERVER}/post_catalog_search/${product.itemId}/${user}`, user, password, encodedAuth);
        tempProduct = mergeObjects(tempProduct, catalogSearch);
      } catch (error) {
        console.error(`Failed to fetch from post_catalog_search for Item Id: ${product.itemId}:`, error.message);
      }

      setCurrProduct(tempProduct);
      setIsLoading(false);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    if (activeStep === 3) {
      setActiveStep(1);
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  const getStep = () => {
    if (activeStep === 0) {
      return (
        <Box sx={{ textAlign: 'center' }}>
          <NewBatchModal
            open={newBatchOpen} setOpen={setNewBatchOpen}
            batchName={batchName} setBatchName={setBatchName}
            channel={channel} setChannel={setChannel}
            handleNext={handleNext}
          />
        </Box>
      )
    } else if (activeStep === 1) {
      return (
        <>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', }}>
              <CustomCircularProgress />
            </Box>
          ) : (
            <SearchedProductsTable products={buyListItems} setCurrProduct={setCurrProduct} isLoading={isLoading} handleNext={handleNext} />
          )}
        </>
      )
    } else if (activeStep === 2) {
      return (
        <>
          {channel === "wfs" ? (
            <Form currProduct={currProduct} barcode={currProduct.upc} handleAddItem={handleAddItem} batchName={batchName} skuUpdate={skuUpdate} setSkuUpdate={setSkuUpdate} multiple={true} />
          ) : (
            <FormSF currProduct={currProduct} barcode={currProduct.upc} handleAddItem={handleAddItem} batchName={batchName} multiple={true} />
          )}
        </>
      )
    } else if (activeStep === 3) {
      return (
        <>
          {startProcess ? (
            <Box sx={styles.processBox}>
              <Typography variant="h6" gutterBottom sx={{ color: theme.text.primary, fontFamily: theme.fontFamily, }}>
                Bulk Upload & Conversion Process
              </Typography>
              <List>
                {processSteps.map((step, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      {processStatusIcons[step.status] || processStatusIcons.pending}
                    </ListItemIcon>
                    <ListItemText primary={<Typography variant="body2" sx={{ color: theme.text.primary, fontFamily: theme.fontFamily, }}>{step.name}</Typography>} />
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : (
            <Box sx={styles.processBox}>
              <Typography variant="h6" gutterBottom sx={{ color: theme.text.primary, fontFamily: theme.fontFamily, textAlign: 'center', }}>
                Once you start, you won’t be able to add or edit items in this bulk.
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ color: theme.text.primary, fontFamily: theme.fontFamily, textAlign: 'center', }}>
                The upload process will begin immediately after clicking the start button.
              </Typography>
              <Button
                variant="contained"
                sx={{ ...styles.navigationButton, width: '50%' }}
                onClick={handleStartProccess}
                startIcon={<PlayCircleOutlineIcon />}
              >
                Start
              </Button>
            </Box>
          )}
        </>
      )
    }
  };

  const changeQty = async (value, item) => {
    if (value === "") {
      return;
    }
    try {
      const body = {
        sku: item.sku,
        email: user,
        qty: parseInt(value)
      }
      item.qty = parseInt(value);
      fetchWrapperNoAuth(`${SERVER}/falcon_update_qty`, user, password, { method: 'POST', body: body });
    } catch (error) {
      console.error('Error update batch:', error.message);
    }
  };

  // Items Proccess Pipeline
  const handleStartProccess = async () => {
    if (addedItems.length > 0) {
      setStartProcess(true);
      startBulkProcess();
    } else {
      alertError("Please add items before starting the process.")
    }
  };

  const startBulkProcess = async () => {
    // Reset all steps to pending
    setProcessSteps(prevSteps =>
      prevSteps.map(step => ({ ...step, status: "pending" }))
    );
    const result = await runBulkProcessSteps(
      addedItems,
      setProcessSteps,
      setAddedItems,
      user,
      password,
      encodedAuth,
      showModal,
      "MarterBuyListsBulk.js",
      1,
      channel,
    );

    if (result.success && result.processComplete) {
      // Process completed successfully
      console.log(`Process completed with ${result.items.length} items`);
      if (isSubUser) {
        addToSentItemsLSBulk(user, result.items);
      }
    } else {
      // Process failed or was cancelled
      alertError(`Process failed at step ${result.step}: ${result.error}`);
    }
  };

  useEffect(() => {
    const getBuyList = async () => {
      setIsLoading(true);
      const url = `https://server.getmarter.com/marter_app_get_buy_list/${user}/`;

      try {
        const response = await fetch(url);
        const data = await response.json();
        if (data === "No permission") {
          setIsLoading(false);
          alertError(
            <Box>
              <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: 18, }}>No permission to Marter.</Typography>
              <Typography onClick={() => window.open("https://www.getmarter.com/")} sx={{ cursor: "pointer", fontFamily: theme.fontFamily, textDecoration: "underline", color: theme.text.primary, fontSize: 14, }}>click here to register to marter</Typography>
            </Box>
          );
          return;
        }

        const mappedData = data.map((item) => {
          return {
            itemId: item.itemId,
            title: item.itemName,
            price: {
              amount: item.sell,
              currency: "USD"
            },
            upc: item.itemUPC,
            cost: item.cost,
            image: item.img,
            note: item.notes,
            qty: item.qty,
            dbId: item.id
          }
        });
        // Ensure uniqueness by itemId and keep the item with the lowest cost
        const uniqueData = mappedData.reduce((acc, currentItem) => {
          const existingItem = acc.find((item) => item.itemId === currentItem.itemId);

          if (!existingItem) {
            acc.push(currentItem);
          } else if (currentItem.cost < existingItem.cost) {
            // If current item has a lower cost, replace the existing one
            acc.splice(acc.indexOf(existingItem), 1, currentItem);
          }

          return acc;
        }, []);

        setCurrProduct(uniqueData[0])
        setBuyListItems(uniqueData);
        setIsLoading(false);
        uniqueData.forEach(item => prefetchItemData(item));
      } catch (error) {
        console.error(`All Batches request failed:`, error.message);
        setIsLoading(false);
        return;
      }
    }
    getBuyList();
  }, []);

  const styles = {
    root: {
      display: 'flex',
      height: '100%',
      bgcolor: theme.background.primary,
    },
    mainContent: {
      flex: 1,
      p: 3,
      overflow: 'auto'
    },
    sidebar: {
      width: 320,
      borderLeft: `1px solid ${theme.border.medium}`,
      display: 'flex',
      flexDirection: 'column',
      bgcolor: theme.background.secondary,
      height: `calc(100vh - ${props.navbarHeight})`
    },
    itemsList: {
      flex: 1,
      overflowY: 'auto',
      marginRight: '5px',
      '&::-webkit-scrollbar': {
        width: '10px', // Width of the scrollbar
        height: '10px', // Width of the scrollbar
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: theme.background.primary, // Track color
        borderRadius: '15px', // Optional: rounded corners
        border: `1px solid ${theme.border.light}`,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.scrollbar, // Thumb color
        borderRadius: '15px', // Optional: rounded corners
        cursor: 'grab',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: theme.scrollbarHover, // Thumb color on hover
      },
      '&::-webkit-scrollbar-button': {
        display: 'none', // Thumb color on hover
      },
    },
    buttonContainer: {
      p: 2,
      borderTop: `1px solid ${theme.border.medium}`,
      bgcolor: theme.background.secondary,
    },
    navigationButton: {
      width: '100%',
      mb: 1,
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      border: `1px solid ${theme.border.light}`,
      borderRadius: '10px',
      color: theme.text.negative,
      backgroundColor: theme.color.primary,
    },
    glowButton: {
      width: '100%',
      mb: 1,
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      border: `1px solid ${theme.border.light}`,
      borderRadius: '10px',
      color: theme.text.negative,
      backgroundColor: theme.color.primary,
      boxShadow: `0 0 10px 3px ${theme.accent.warning}`,
      '&:hover': {
        boxShadow: `0 0 15px 5px ${theme.accent.warning}`,
        backgroundColor: theme.color.primary,
      },
      transition: 'box-shadow 0.3s ease-in-out',
    },
    processBox: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      p: 3,
      border: `1px solid ${theme.border.light}`,
      borderRadius: 2,
      bgcolor: theme.background.secondary,
      width: 'auto',
      mx: 'auto',
      mt: '10%'
    }
  };

  if (!encodedAuth || encodedAuth === "") {
    alert("Please enter you credentials (Client Id & Client Secret from Walmart)");
    navigate('/account');
  }

  return (
    <Box sx={styles.root}>
      {/* Main Content Area */}
      <Box sx={styles.mainContent}>
        <Typography variant="h5" sx={{ textAlign: 'center', mb: 3, fontFamily: theme.fontFamily, color: theme.text.primary }}>
          Add multiple items from Marter Buy List
        </Typography>

        {/* Stepper */}
        <CustomStepper activeStep={activeStep} steps={steps} />

        {/* Main form content */}
        <Box sx={{ mt: 2, }}>
          {getStep()}
        </Box>
      </Box>

      {/* Sidebar */}
      <ResizableSidebar width={sidebarWidth} setWidth={setSidebarWidth}>
        {/* Added Items List */}
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.border.primary}`, color: theme.text.primary, }}>
          <Typography variant="h6" sx={{ cursor: 'pointer', fontFamily: theme.fontFamily }} onClick={() => { if (activeStep === 0) setNewBatchOpen(true) }}>
            {batchName !== "none" && batchName}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontFamily: theme.fontFamily }}>
              Added Items ({addedItems.length})
            </Typography>
            <Box sx={{ display: 'flex' }}>
              <HtmlTooltip
                customtheme={theme}
                title={
                  <Typography sx={{ color: theme.text.primary, fontFamily: theme.fontFamily, fontSize: 12 }}>
                    Save batches to the cloud
                  </Typography>
                }
                arrow
                placement={batchName !== "" ? "top" : "bottom"}
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -15],
                        },
                      },
                    ],
                  },
                }}
              >
                <IconButton
                  onClick={handleSaveBatches}
                  sx={{ color: theme.accent.success, }}
                >
                  <CloudUpload />
                </IconButton>
              </HtmlTooltip>
              <HtmlTooltip
                customtheme={theme}
                title={
                  <Typography sx={{ color: theme.text.primary, fontFamily: theme.fontFamily, fontSize: 12 }}>
                    Load batches from the cloud
                  </Typography>
                }
                arrow
                placement={batchName !== "" ? "top" : "bottom"}
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -15],
                        },
                      },
                    ],
                  },
                }}
              >
                <IconButton
                  onClick={handleLoadBatches}
                  sx={{ color: theme.color.primary, }}
                >
                  <CloudDownload />
                </IconButton>
              </HtmlTooltip>
            </Box>
          </Box>
        </Box>

        <Box sx={styles.itemsList}>
          {addedItems.map((item, index) => (
            <ResponsiveItemCard
              key={index}
              item={item}
              index={index}
              theme={theme}
              onRetry={retryAddItem}
              changeQty={changeQty}
              editItem={editItem}
              setItemToModify={setItemToModify}
              setOpenDeleteFromBatchModal={setOpenDeleteFromBatchModal}
              setBarcodeLabelProduct={setBarcodeLabelProduct}
              setOpenBarcodeModal={setOpenBarcodeModal}
              sidebarWidth={sidebarWidth}
            />
          ))}
        </Box>

        <Box sx={{ ml: '10px', mt: '10px' }}>
          {(() => {
            const sellPrice = addedItems.reduce((sum, item) => sum + ((item.price?.amount ?? 0) * (item.qty || 1)), 0);
            const costPrice = addedItems.reduce((sum, item) => sum + ((item.cost || 0) * (item.qty || 1)), 0);
            const totalRefferalFees = addedItems.reduce((sum, item) => sum + ((calcRefFees(item)) * (item.qty || 1)), 0);
            const totalWfsCost = addedItems.reduce((sum, item) => sum + ((calcWfs(item) + (((item.price?.amount ?? 0) && (item.price?.amount ?? 0) < 10) ? 1 : 0)) * (item.qty || 1)), 0);
            const feesPrice = totalRefferalFees + totalWfsCost;
            const profit = sellPrice - costPrice - feesPrice;
            const totalUnits = addedItems.reduce((sum, item) => sum + (item.qty || 1), 0);

            return (
              <>
                <Typography variant="body2" sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, }}>
                  Total units: {totalUnits}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, }}>
                  Total sell price: ${sellPrice.toFixed(2)}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, }}>
                  Total cost price: ${costPrice.toFixed(2)}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: theme.fontFamily, color: profit > 0 ? theme.accent.success : theme.accent.error }}>
                  Profit: {profit < 0 && "-"}${profit < 0 ? (profit * -1).toFixed(2) : profit.toFixed(2)}
                </Typography>
              </>
            )
          })()}
        </Box>

        {/* Navigation Buttons */}
        <Box sx={styles.buttonContainer}>
          {activeStep === 0 && !batchName && (
            <Button
              variant="contained"
              sx={{
                ...styles.navigationButton,
                backgroundColor: theme.accent.success,
              }}
              onClick={() => setNewBatchOpen(true)}
            >
              Create New Batch
            </Button>
          )}
          <Button
            variant="outlined"
            sx={{
              ...styles.navigationButton,
              color: theme.color.primary,
              backgroundColor: theme.background.primary,
              "&.Mui-disabled": {
                color: 'gray',
              },
            }}
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
            disabled={activeStep === 0}
          >
            {activeStep === 1 ? "Change Batch Name" : "Previous Step"}
          </Button>

          {activeStep === 2 && (
            <Button
              variant="contained"
              sx={styles.navigationButton}
              onClick={handleAddAnotherItem}
              startIcon={<AddIcon />}
            >
              Add Another Item
            </Button>
          )}

          {activeStep !== steps.length - 1 && (
            <Button
              variant="contained"
              sx={styles.navigationButton}
              onClick={() => handleNext(currProduct)}
              endIcon={<ArrowForwardIcon />}
            >
              Next Step
            </Button>
          )}
          {addedItems.length > 0 && addedItems.every(item => item.status !== "LOADING" && item.status !== "PENDING") ? (
            <Button
              variant="contained"
              sx={styles.glowButton}
              onClick={handleSendAllItems}
              startIcon={<SendIcon />}
            >
              {channel === "sf" ? "Add" : "Send"} All Items ({addedItems.filter(item => item.status === "SUCCESS").length})
            </Button>
          ) : (
            <Button
              variant="contained"
              sx={styles.navigationButton}
              onClick={() => setActiveStep(3)}
              startIcon={<SendIcon />}
            >
              Start Process
            </Button>
          )}
        </Box>
      </ResizableSidebar>

      <Snackbar
        open={alertBatch.open}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}  // Position at the top-center
        autoHideDuration={3000}
        onClose={() => setAlertBatch({ ...alertBatch, open: false })}
        sx={{ marginTop: props.navbarHeight }}  // Add margin of 10vh from the top
      >
        <Alert severity={alertBatch.severity} sx={{ width: "100%", zIndex: 999, fontFamily: theme.fontFamily }}>
          {alertBatch.message}
        </Alert>
      </Snackbar>

      <QuestionModal
        open={openDeleteFromBatchModal}
        setOpen={setOpenDeleteFromBatchModal}
        message={
          <Box>
            <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: '16px', }}>This item will be removed only from your batch, not from your inventory.</Typography>
            <br />
            <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: '16px', fontWeight: 'bold', }}>Are you sure you want to proceed?</Typography>
          </Box>
        }
        confirm={() => { removeFromBatch(itemToModify); setOpenDeleteFromBatchModal(false); }}
        cancel={() => setOpenDeleteFromBatchModal(false)}
      />

      <BatchSelectionModal
        open={openConfirmModal}
        setOpen={setOpenConfirmModal}
        batchesArray={batchesArray}
        handleSelectBatch={(batchSelected) => loadStatesFromLocalStorage(batchSelected)}
        removeBatch={(batchSelected) => clearStoredData(batchSelected)}
        cancel={() => { setLoadStates(true); setOpenConfirmModal(false); }}
      />

      <BarcodeLabelModal open={openBarcodeModal} setOpen={setOpenBarcodeModal} product={barcodeLabelProduct} />

      <ErrorModal open={openErrorModal} setOpen={setopenErrorModal} message={errorMessage} />

      <ShippmentsModal open={shippmentOpenModal} setOpen={setShippmentOpenModal} items={addedItems.filter(item => item.status === "SUCCESS")} clearStoredData={() => clearStoredData(batchName)} />
    </Box>
  );
}

export default MarterBuyListsBulk;
